<template>
  <footer class="app-footer">
    <div class="footer-container">
      <!-- Main Footer Content -->
      <div class="footer-main">
        <div class="footer-brand">
          <div class="brand-info">
            <div v-if="logo" class="brand-logo">
              <img :src="logo" alt="Logo" />
            </div>
            <div v-else class="brand-placeholder">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 9.5L12 4L21 9.5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M19 13V19.4C19 19.7314 18.7314 20 18.4 20H5.6C5.26863 20 5 19.7314 5 19.4V13"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <p class="brand-tagline">
              Tu portal exclusivo para gestionar tus propiedades
            </p>
          </div>
        </div>

        <div class="footer-links">
          <div class="links-group">
            <h4 class="group-title">Navegación</h4>
            <a @click="goToProperties" class="footer-link">
              {{ customer === "clab" ? "Mis Inversiones" : "Mis Propiedades" }}
            </a>
            <router-link
              v-if="showQuotes"
              :to="{ name: 'myquotes' }"
              class="footer-link"
              @click.native="scrollToTop"
            >
              {{ customer === "clab" ? "Simulaciones" : "Cotizaciones" }}
            </router-link>
            <router-link
              v-if="showMyData"
              :to="{ name: 'information' }"
              class="footer-link"
              @click.native="scrollToTop"
            >
              Mi Perfil
            </router-link>
          </div>
        </div>
      </div>

      <!-- Footer Bottom -->
      <div class="footer-bottom">
        <div class="copyright">
          <span>© {{ year }} Todos los derechos reservados</span>
        </div>
        <div class="powered-by">
          <span>Desarrollado con</span>
          <svg
            class="heart-icon"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path
              d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z"
            />
          </svg>
          <span>por</span>
          <a
            href="https://mobysuite.com"
            target="_blank"
            class="mobysuite-link"
          >
            <strong class="mobysuite-text">Mobysuite</strong>
          </a>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
export default {
  name: "Footer",
  data() {
    return {
      user: this.$auth.user(),
      year: new Date().getFullYear(),
    };
  },
  methods: {
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    goToProperties() {
      this.scrollToTop();
      // Emit event to go back to grid and navigate to index
      this.$root.$emit("go-back-to-grid");
      if (this.$route.name !== "index") {
        this.$router.push({ name: "index" });
      }
    },
  },
  computed: {
    logo() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.logo_1_url
        : null;
    },
    customer() {
      return this.$store.getters.getCustomer;
    },
    showQuotes() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.show_quotes
        : false;
    },
    showMyData() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.show_my_data
        : false;
    },
  },
};
</script>

<style scoped>
/* ===== Premium Footer Styles ===== */
.app-footer {
  /* Variables dinámicas basadas en el tema de la inmobiliaria */
  --theme-primary: var(--color-btn, #3b82f6);
  --nav-bg: var(--color-menu, #ffffff);
  --nav-text: var(--color-menu-text, #374151);
  --nav-text-muted: color-mix(in srgb, var(--nav-text) 70%, transparent);

  background: var(--nav-bg);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  margin-top: auto;
  color: var(--nav-text);
}

.footer-container {
  width: var(--classic-container-width, 100%);
  max-width: var(--classic-container-max, 1400px);
  margin-left: auto;
  margin-right: auto;
  padding: 48px var(--classic-container-pad-x, 24px) 24px;
  box-sizing: border-box;
}

/* Main Footer */
.footer-main {
  display: flex;
  justify-content: space-between;
  gap: 48px;
  padding-bottom: 32px;
  border-bottom: 1px solid color-mix(in srgb, var(--nav-text) 15%, transparent);
}

.footer-brand {
  max-width: 300px;
}

.brand-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.brand-logo img {
  max-height: 36px;
  width: auto;
  object-fit: contain;
}

.brand-placeholder {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--theme-primary);
  border-radius: 10px;
  color: #ffffff;
}

.brand-tagline {
  font-size: 0.875rem;
  color: var(--nav-text-muted);
  line-height: 1.5;
  margin: 0;
}

/* Footer Links */
.footer-links {
  display: flex;
  gap: 64px;
}

.links-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.group-title {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--nav-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.footer-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: var(--nav-text);
  text-decoration: none;
  transition: color 0.15s ease;
}

.footer-link:hover {
  color: var(--theme-primary);
  text-decoration: none;
}

.footer-link svg {
  color: var(--nav-text-muted);
  transition: color 0.15s ease;
}

.footer-link:hover svg {
  color: var(--theme-primary);
}

/* Footer Bottom */
.footer-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 24px;
}

.copyright {
  font-size: 0.8125rem;
  color: var(--nav-text-muted);
}

.powered-by {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  color: var(--nav-text-muted);
}

.heart-icon {
  color: #ef4444;
  animation: heartbeat 1.5s ease-in-out infinite;
}

@keyframes heartbeat {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.mobysuite-link {
  display: flex;
  align-items: center;
  margin-left: 4px;
  text-decoration: none;
  color: inherit;
}

.mobysuite-text {
  font-weight: 700;
  color: inherit;
  transition: opacity 0.15s ease;
}

.mobysuite-link:hover .mobysuite-text {
  opacity: 0.8;
}

/* Responsive */
@media (max-width: 768px) {
  .footer-container {
    padding: 32px 20px 20px;
  }

  .footer-main {
    flex-direction: column;
    gap: 32px;
  }

  .footer-brand {
    max-width: 100%;
  }

  .footer-links {
    flex-direction: column;
    gap: 24px;
  }

  .footer-bottom {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }

  .group-title {
    font-size: 0.7rem;
  }

  .footer-link {
    font-size: 0.85rem;
  }

  .brand-tagline {
    font-size: 0.85rem;
  }
}

@media (max-width: 576px) {
  .footer-container {
    padding: 24px 16px 16px;
  }

  .footer-main {
    gap: 24px;
  }

  .footer-links {
    gap: 20px;
  }

  .footer-bottom {
    gap: 12px;
  }

  .copyright {
    font-size: 0.75rem;
  }

  .powered-by {
    font-size: 0.75rem;
  }

  .brand-logo img {
    max-height: 36px;
  }

  .group-title {
    margin-bottom: 10px;
  }

  .links-group {
    gap: 6px;
  }

  .footer-link {
    font-size: 0.8rem;
    padding: 4px 0;
  }
}
</style>
